<template>
  <section class="word-detail-page">
    <header class="detail-hero">
      <button type="button" class="back-btn" @click="goBack">
        {{ t("back") }}
      </button>

      <div class="hero-copy">
        <p class="hero-eyebrow">{{ t("eyebrow") }}</p>
        <h1 class="hero-title">{{ word.word_text }}</h1>
        <div class="hero-meta">
          <span>{{ pronunciation }}</span>
          <span>{{ word.word_id }}</span>
          <button type="button" class="speak-btn" @click="speakEnglish(word.word_text)">
            {{ t("speakWord") }}
          </button>
        </div>
      </div>

      <div class="hero-tools">
        <div class="lang-pill">
          <button
            v-for="choice in langs"
            :key="choice"
            type="button"
            :class="['lang-chip', { active: choice === lang }]"
            @click="setLang(choice)"
          >
            {{ choice.toUpperCase() }}
          </button>
        </div>

        <div class="study-actions">
          <button type="button" :class="['study-chip', { active: studyState.viewed }]" @click="noop">
            {{ t("viewed") }} {{ studyState.viewCount || 1 }}
          </button>
          <button
            type="button"
            :class="['study-chip', { active: studyState.favorite }]"
            @click="toggleFlag('favorite')"
          >
            {{ t("favorite") }}
          </button>
          <button
            type="button"
            :class="['study-chip', { active: studyState.mastered }]"
            @click="toggleFlag('mastered')"
          >
            {{ t("mastered") }}
          </button>
        </div>
      </div>
    </header>

    <div v-if="loading" class="state-card">{{ t("loading") }}</div>
    <div v-else-if="errorMessage" class="state-card error-card">{{ errorMessage }}</div>
    <template v-else>
      <section class="content-grid">
        <article class="panel-card structure-panel">
          <div class="section-head">
            <span class="panel-tag">{{ t("structureTag") }}</span>
            <h2 class="section-title">{{ t("structureTitle") }}</h2>
            <p class="section-copy">{{ t("structureCopy") }}</p>
          </div>

          <div class="structure-map">
            <button
              v-for="part in structureParts"
              :key="part.index"
              type="button"
              :class="['part-chip', { active: part.index === activePartIndex }]"
              @click="activePartIndex = part.index"
            >
              <span class="part-label">{{ part.label }}</span>
              <strong>{{ part.text }}</strong>
            </button>
          </div>

          <article v-if="activePart" class="active-part-card">
            <p class="part-kicker">{{ activePart.label }}</p>
            <h3>{{ activePart.text }}</h3>
            <ul class="part-meanings">
              <li><strong>EN</strong> {{ activePart.meaningEn }}</li>
              <li><strong>JA</strong> {{ activePart.meaningJa }}</li>
              <li><strong>ZH</strong> {{ activePart.meaningZh }}</li>
            </ul>
          </article>

          <div class="word-meaning-card">
            <span class="panel-tag">{{ t("wholeMeaning") }}</span>
            <p>{{ getWordMeaning(word, lang) }}</p>
          </div>
        </article>

        <article class="panel-card root-panel">
          <div class="section-head compact-head">
            <span class="panel-tag">{{ t("rootTag") }}</span>
            <h2 class="section-title">{{ t("rootTitle") }}</h2>
            <p class="section-copy">{{ t("rootCopy") }}</p>
          </div>

          <button v-if="root" type="button" class="root-summary" @click="openRoot(root.root_id)">
            <div>
              <strong>{{ root.root_text }}</strong>
              <p>{{ getRootMeaning(root, lang) }}</p>
            </div>
            <span>{{ root.root_id }}</span>
          </button>
          <p v-else class="empty-state">{{ t("noRoot") }}</p>

          <div class="meta-grid">
            <article class="meta-card">
              <span>{{ t("familySize") }}</span>
              <strong>{{ wordFamily.length }}</strong>
            </article>
            <article class="meta-card">
              <span>{{ t("updatedAt") }}</span>
              <strong>{{ formatDate(word.updated_at) }}</strong>
            </article>
          </div>

          <div class="video-block">
            <div class="video-head">
              <span class="panel-tag">{{ t("videoTag") }}</span>
              <button type="button" class="toggle-btn" @click="videoExpanded = !videoExpanded">
                {{ videoExpanded ? t("hideVideo") : t("showVideo") }}
              </button>
            </div>

            <div v-if="videoExpanded" class="video-body">
              <div class="video-switches">
                <button
                  type="button"
                  :class="['switch-btn', { active: selectedPlatform === 'youtube' }]"
                  @click="setPlatform('youtube')"
                >
                  YouTube
                </button>
                <button
                  type="button"
                  :class="['switch-btn', { active: selectedPlatform === 'bilibili' }]"
                  @click="setPlatform('bilibili')"
                >
                  bilibili
                </button>
                <button
                  type="button"
                  :class="['switch-btn', { active: selectedMediaLang === 'ja' }]"
                  @click="setMediaLang('ja')"
                >
                  JA
                </button>
                <button
                  type="button"
                  :class="['switch-btn', { active: selectedMediaLang === 'zh' }]"
                  @click="setMediaLang('zh')"
                >
                  ZH
                </button>
              </div>

              <div v-if="currentVideo.embedUrl" class="video-frame-wrap">
                <iframe
                  :src="currentVideo.embedUrl"
                  class="video-frame"
                  allowfullscreen
                  referrerpolicy="strict-origin-when-cross-origin"
                />
              </div>
              <p v-else class="empty-state">{{ t("noVideo") }}</p>
              <a v-if="currentVideo.openUrl" :href="currentVideo.openUrl" target="_blank" rel="noopener" class="video-link">
                {{ t("openVideo") }}
              </a>
            </div>
          </div>
        </article>
      </section>

      <section class="content-grid">
        <article class="panel-card">
          <div class="section-head compact-head">
            <span class="panel-tag">{{ t("meaningsTag") }}</span>
            <h2 class="section-title">{{ t("meaningsTitle") }}</h2>
            <p class="section-copy">{{ t("meaningsCopy") }}</p>
          </div>

          <div class="meaning-stack">
            <article v-for="(item, index) in meaningItems" :key="index" class="meaning-card">
              <div class="meaning-head">
                <strong>{{ t("meaningIndex") }} {{ index + 1 }}</strong>
                <button type="button" class="speak-btn tiny" @click="speakEnglish(item.sentence_en || word.word_text)">
                  {{ t("speakSentence") }}
                </button>
              </div>
              <p class="meaning-en">{{ item.meaning_en || "-" }}</p>
              <ul class="meaning-translations">
                <li><strong>JA</strong> {{ item.meaning_jp || "-" }}</li>
                <li><strong>ZH</strong> {{ item.meaning_ch || "-" }}</li>
              </ul>
              <div class="sentence-box">
                <p class="sentence-en">{{ item.sentence_en || "-" }}</p>
                <ul class="meaning-translations">
                  <li><strong>JA</strong> {{ item.sentence_jp || "-" }}</li>
                  <li><strong>ZH</strong> {{ item.sentence_ch || "-" }}</li>
                </ul>
              </div>
            </article>
          </div>
        </article>

        <article class="panel-card">
          <div class="section-head compact-head">
            <span class="panel-tag">{{ t("familyTag") }}</span>
            <h2 class="section-title">{{ t("familyTitle") }}</h2>
            <p class="section-copy">{{ t("familyCopy") }}</p>
          </div>

          <div class="family-toolbar">
            <label>
              {{ t("sortBy") }}
              <select v-model="familySort">
                <option value="alpha">{{ t("sortAlpha") }}</option>
                <option value="recent">{{ t("sortRecent") }}</option>
              </select>
            </label>
          </div>

          <div class="family-list">
            <button
              v-for="item in sortedFamily"
              :key="item.word_id"
              type="button"
              :class="['family-item', { active: item.word_id === word.word_id }]"
              @click="openWord(item.word_id)"
            >
              <div>
                <strong>{{ item.word_text }}</strong>
                <p>{{ getWordMeaning(item, lang) }}</p>
              </div>
              <span>{{ getPronunciation(item) }}</span>
            </button>
          </div>
        </article>
      </section>
    </template>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import {
  API_BASE,
  ensureApiObject,
  formatDate,
  getMeaningItems,
  getPronunciation,
  getRootMeaning,
  getStructureParts,
  getWordMeaning,
  getWordStudyState,
  recordWordVisit,
  speakEnglish,
  toggleWordFlag,
} from "./englishStudyShared";

const route = useRoute();
const router = useRouter();
const { lang, languages: langs, setLang } = useSiteLanguage();
const loading = ref(false);
const errorMessage = ref("");
const detail = ref({ word: null, root: null, word_family: [] });
const activePartIndex = ref(0);
const studyState = ref({ viewed: false, favorite: false, mastered: false, viewCount: 0 });
const familySort = ref("alpha");

const videoExpanded = ref(false);
const selectedPlatform = ref(localStorage.getItem("english_word_video_platform") || "youtube");
const selectedMediaLang = ref(localStorage.getItem("english_word_video_lang") || "ja");

const word = computed(() => detail.value.word || {});
const root = computed(() => detail.value.root || null);
const wordFamily = computed(() => Array.isArray(detail.value.word_family) ? detail.value.word_family : []);
const meaningItems = computed(() => getMeaningItems(word.value));
const structureParts = computed(() => getStructureParts(word.value, lang.value));
const activePart = computed(() => structureParts.value[activePartIndex.value] || structureParts.value[0] || null);
const pronunciation = computed(() => getPronunciation(word.value));

const currentVideo = computed(() => {
  const ids =
    selectedPlatform.value === "youtube"
      ? (word.value.youtube_video_id || [])
      : (word.value.bilibili_video_id || []);

  const picked = selectedMediaLang.value === "zh" ? ids[1] || ids[0] : ids[0] || ids[1];
  if (!picked) {
    return { embedUrl: "", openUrl: "" };
  }

  if (selectedPlatform.value === "youtube") {
    return {
      embedUrl: `https://www.youtube.com/embed/${encodeURIComponent(picked)}`,
      openUrl: `https://www.youtube.com/watch?v=${encodeURIComponent(picked)}`,
    };
  }

  return {
    embedUrl: `https://player.bilibili.com/player.html?bvid=${encodeURIComponent(picked)}&page=1`,
    openUrl: `https://www.bilibili.com/video/${encodeURIComponent(picked)}`,
  };
});

const sortedFamily = computed(() => {
  const list = [...wordFamily.value];
  if (familySort.value === "recent") {
    return list.sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
  }

  return list.sort((a, b) => String(a.word_text || "").localeCompare(String(b.word_text || "")));
});

const i18n = {
  ja: {
    back: "Back",
    eyebrow: "WORD DETAIL",
    viewed: "見た",
    favorite: "お気に入り",
    mastered: "習得済み",
    loading: "Loading...",
    structureTag: "STRUCTURE",
    structureTitle: "構造で覚える",
    structureCopy: "prefix / root / suffix を押して、それぞれの意味を確認できます。",
    wholeMeaning: "単語全体の意味",
    rootTag: "ROOT LINK",
    rootTitle: "つながる語根",
    rootCopy: "この単語が属する語根と更新情報です。",
    noRoot: "関連語根はありません。",
    familySize: "同語根単語数",
    updatedAt: "更新日",
    videoTag: "VIDEO",
    showVideo: "動画を開く",
    hideVideo: "動画を閉じる",
    noVideo: "動画はまだありません。",
    openVideo: "新しいタブで開く",
    meaningsTag: "MEANINGS",
    meaningsTitle: "意味と例文",
    meaningsCopy: "例文ごとに意味を確認し、英語音声も再生できます。",
    meaningIndex: "Meaning",
    speakWord: "Read",
    speakSentence: "Speak",
    familyTag: "WORD FAMILY",
    familyTitle: "同じ語根の単語",
    familyCopy: "同じ語根に属する単語へそのまま切り替えて見ていけます。",
    sortBy: "並び順",
    sortAlpha: "A-Z",
    sortRecent: "更新順",
  },
  zh: {
    back: "Back",
    eyebrow: "WORD DETAIL",
    viewed: "已看",
    favorite: "收藏",
    mastered: "已掌握",
    loading: "Loading...",
    structureTag: "STRUCTURE",
    structureTitle: "按结构记忆",
    structureCopy: "点击 prefix / root / suffix，就能分别查看它们的含义。",
    wholeMeaning: "整词意义",
    rootTag: "ROOT LINK",
    rootTitle: "关联词根",
    rootCopy: "这里显示这个单词所属的词根和最近更新时间。",
    noRoot: "没有关联词根。",
    familySize: "同词根单词数",
    updatedAt: "更新时间",
    videoTag: "VIDEO",
    showVideo: "打开视频",
    hideVideo: "收起视频",
    noVideo: "还没有视频。",
    openVideo: "新标签页打开",
    meaningsTag: "MEANINGS",
    meaningsTitle: "意义与例句",
    meaningsCopy: "可以逐条查看意义，并直接播放英文例句。",
    meaningIndex: "义项",
    speakWord: "朗读",
    speakSentence: "播放句子",
    familyTag: "WORD FAMILY",
    familyTitle: "同词根词族",
    familyCopy: "可以直接切换到同一词根下的其他单词继续学习。",
    sortBy: "排序",
    sortAlpha: "字母顺",
    sortRecent: "最近更新",
  },
  en: {
    back: "Back",
    eyebrow: "WORD DETAIL",
    viewed: "Viewed",
    favorite: "Favorite",
    mastered: "Mastered",
    loading: "Loading...",
    structureTag: "STRUCTURE",
    structureTitle: "Learn by Structure",
    structureCopy: "Tap each prefix, root, or suffix to inspect its meaning.",
    wholeMeaning: "Whole-word meaning",
    rootTag: "ROOT LINK",
    rootTitle: "Linked Root",
    rootCopy: "This word's root and update context.",
    noRoot: "No linked root.",
    familySize: "Family Size",
    updatedAt: "Updated",
    videoTag: "VIDEO",
    showVideo: "Open Video",
    hideVideo: "Hide Video",
    noVideo: "No video yet.",
    openVideo: "Open in new tab",
    meaningsTag: "MEANINGS",
    meaningsTitle: "Meanings and Sentences",
    meaningsCopy: "Review each sense and play the English sentence out loud.",
    meaningIndex: "Meaning",
    speakWord: "Read",
    speakSentence: "Speak",
    familyTag: "WORD FAMILY",
    familyTitle: "Same-root Family",
    familyCopy: "Switch directly to other words under the same root.",
    sortBy: "Sort by",
    sortAlpha: "Alphabet",
    sortRecent: "Recent update",
  },
};

function t(key) {
  return i18n[lang.value]?.[key] || key;
}

function setPlatform(value) {
  selectedPlatform.value = value;
  localStorage.setItem("english_word_video_platform", value);
}

function setMediaLang(value) {
  selectedMediaLang.value = value;
  localStorage.setItem("english_word_video_lang", value);
}

function noop() {
  return null;
}

function toggleFlag(flag) {
  toggleWordFlag(word.value, flag);
  studyState.value = getWordStudyState(word.value.word_id);
}

async function loadWordDetail() {
  const key = decodeURIComponent(String(route.params.wordId || "")).trim();
  if (!key) {
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await axios.get(`${API_BASE}/api/english/words/${encodeURIComponent(key)}`, {
      timeout: 10000,
    });
    detail.value = ensureApiObject(response, "English word detail API");
    activePartIndex.value = 0;
    recordWordVisit(detail.value.word);
    studyState.value = getWordStudyState(detail.value.word.word_id);
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.message || "Failed to load word detail.";
  } finally {
    loading.value = false;
  }
}

function openRoot(rootId) {
  router.push(`/english/root/${encodeURIComponent(rootId)}`);
}

function openWord(wordId) {
  router.push(`/english/word/${encodeURIComponent(wordId)}`);
}

function goBack() {
  router.push("/english/englishdectionary");
}

onMounted(loadWordDetail);
watch(() => route.params.wordId, loadWordDetail);
</script>

<style scoped>
.word-detail-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.12), transparent 26%),
    linear-gradient(180deg, #f5f9ff 0%, #eff6fb 100%);
  color: #163047;
}

.detail-hero,
.panel-card,
.state-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.detail-hero {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.back-btn,
.speak-btn,
.study-chip,
.part-chip,
.root-summary,
.toggle-btn,
.switch-btn,
.family-item {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease;
}

.back-btn {
  border: 0;
  border-radius: 16px;
  padding: 12px 16px;
  background: #dceaf8;
  color: #133251;
  font-weight: 700;
}

.hero-eyebrow,
.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.hero-title,
.section-title {
  margin: 0;
  color: #10253a;
}

.hero-title {
  font-size: clamp(34px, 5vw, 64px);
  line-height: 0.95;
}

.hero-meta {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  color: #4f667a;
}

.speak-btn {
  border: 0;
  border-radius: 999px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
  font-weight: 700;
}

.speak-btn.tiny {
  padding: 8px 12px;
  font-size: 12px;
}

.hero-tools {
  display: grid;
  gap: 14px;
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(43, 90, 132, 0.14);
  justify-self: end;
}

.lang-chip {
  border: 0;
  min-width: 54px;
  padding: 10px 14px;
  border-radius: 999px;
  background: transparent;
  color: #47647d;
}

.lang-chip.active {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.study-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: end;
}

.study-chip {
  border: 1px solid rgba(43, 90, 132, 0.12);
  border-radius: 999px;
  padding: 10px 14px;
  background: #eef5fd;
  color: #204568;
}

.study-chip.active {
  background: #183b58;
  color: #fff;
}

.state-card {
  margin-top: 18px;
  padding: 20px;
}

.error-card {
  border-color: rgba(180, 35, 24, 0.18);
  background: rgba(255, 244, 242, 0.94);
  color: #7a271a;
}

.content-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 18px;
}

.panel-card {
  padding: 22px;
}

.section-head {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.section-copy,
.active-part-card,
.word-meaning-card p,
.root-summary p,
.meaning-card p,
.meaning-translations,
.family-item p {
  color: #4c6377;
  line-height: 1.7;
}

.structure-map {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.part-chip {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 20px;
  background: #f6fbff;
  padding: 14px 16px;
  min-width: 132px;
  text-align: left;
}

.part-chip.active {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.part-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.active-part-card,
.word-meaning-card,
.meta-card,
.meaning-card,
.family-item,
.root-summary {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 22px;
  background: #fbfdff;
  padding: 16px;
}

.active-part-card,
.word-meaning-card,
.video-block {
  margin-top: 16px;
}

.part-kicker {
  margin: 0 0 6px;
  color: #35648e;
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.part-meanings,
.meaning-translations {
  margin: 0;
  padding-left: 18px;
}

.root-summary {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  text-align: left;
}

.meta-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.meta-card span {
  display: block;
  color: #4a6580;
  font-size: 13px;
}

.meta-card strong {
  display: block;
  margin-top: 8px;
  color: #0f2840;
  font-size: 24px;
}

.video-head,
.video-switches,
.meaning-head,
.family-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.toggle-btn,
.switch-btn {
  border: 0;
  border-radius: 12px;
  padding: 10px 12px;
  background: #dceaf8;
  color: #133251;
}

.switch-btn.active {
  background: #183b58;
  color: #fff;
}

.video-body {
  margin-top: 12px;
}

.video-frame-wrap {
  margin-top: 12px;
  aspect-ratio: 16 / 9;
  width: 100%;
  background: #000;
  border-radius: 18px;
  overflow: hidden;
}

.video-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.video-link {
  display: inline-block;
  margin-top: 10px;
  color: #0f4f8c;
}

.meaning-stack,
.family-list {
  display: grid;
  gap: 12px;
}

.meaning-en,
.sentence-en {
  margin: 0 0 10px;
  font-size: 18px;
  color: #10253a;
}

.sentence-box {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(43, 90, 132, 0.18);
}

.family-toolbar select {
  margin-left: 8px;
  border: 1px solid rgba(43, 90, 132, 0.18);
  border-radius: 10px;
  padding: 8px 10px;
  background: #fff;
}

.family-item {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  text-align: left;
}

.family-item.active {
  border-color: rgba(15, 79, 140, 0.28);
  background: #eef6ff;
}

.empty-state {
  margin: 0;
  color: #64748b;
}

.back-btn:hover,
.speak-btn:hover,
.study-chip:hover,
.part-chip:hover,
.root-summary:hover,
.toggle-btn:hover,
.switch-btn:hover,
.family-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 1100px) {
  .detail-hero,
  .content-grid {
    grid-template-columns: 1fr;
  }

  .study-actions,
  .lang-pill {
    justify-self: start;
    justify-content: start;
  }
}

@media (max-width: 768px) {
  .word-detail-page {
    padding: 12px;
  }

  .detail-hero,
  .panel-card {
    padding: 18px;
    border-radius: 22px;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }

  .family-item,
  .root-summary {
    flex-direction: column;
  }
}
</style>
